'use client';
import { cva } from 'class-variance-authority';
import { twMerge } from 'tailwind-merge';
import { ButtonHTMLAttributes, ReactNode } from 'react';
import { type VariantProps } from 'class-variance-authority';
import Image from 'next/image';

const iconButtonClasses = cva(
  'inline-flex items-center justify-center transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed hover:scale-105 active:scale-95',
  {
    variants: {
      variant: {
        primary: 'bg-icon-button-bg-light',
        secondary: 'bg-icon-button-bg-accent',
        transparent: 'bg-transparent hover:bg-primary-light',
      },
      size: {
        small: 'p-2',
        medium: 'p-3',
        large: 'p-4',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'medium',
    },
  }
);

interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof iconButtonClasses> {
  src?: string;
  alt?: string;
  width?: string;
  height?: string;
  children?: ReactNode;
}

const IconButton = ({
  src,
  alt = 'Icon',
  width = '24',
  height = '24',
  variant,
  size,
  disabled = false,
  className,
  children,
  onClick,
  type = 'button',
  ...props
}: IconButtonProps) => {
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled) {
      event.preventDefault()
      return
    }
    
    if (typeof onClick === 'function') {
      onClick(event)
    }
  }

  return (
    <button data-component-id="src/components/ui/IconButton.tsx:64:4" data-component-path="src/components/ui/IconButton.tsx" data-component-line="64" data-component-end-line="87" data-component-file="IconButton.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22type%22%3A%22%5Bvar%3Atype%5D%22%2C%22%5Bspread%5D%22%3A%22true%22%7D"
      type={type}
      disabled={disabled}
      onClick={handleClick}
      className={twMerge(
        iconButtonClasses({ variant, size }),
        'rounded-[30px]',
        className
      )}
      aria-disabled={disabled}
      {...props}
    >
      {src ? (
        <Image data-component-id="src/components/ui/IconButton.tsx:77:8" data-component-path="src/components/ui/IconButton.tsx" data-component-line="77" data-component-end-line="83" data-component-file="IconButton.tsx" data-component-name="Image" data-component-content="%7B%22elementName%22%3A%22Image%22%2C%22src%22%3A%22%5Bvar%3Asrc%5D%22%2C%22alt%22%3A%22%5Bvar%3Aalt%5D%22%2C%22className%22%3A%22object-contain%22%7D"
          src={src}
          alt={alt}
          width={parseInt(width)}
          height={parseInt(height)}
          className="object-contain"
        />
      ) : (
        children
      )}
    </button>
  )
}

export default IconButton